import {
  useContext, useEffect, useMemo, useState,
} from 'react';
import Category from '../../../models/Category/Category';
import ModalComponent from '../../../components/ModalComponent';
import { CategoriesContext } from '../../../contexts/CategoriesContext';

interface Props {
  open: boolean
  editCategory: Category | '' | null
  handleClose: () => void
  handleSave: (category: Category) => void
}

const NewCategoryModal = (props: Props) => {
  const {
    open, handleClose, handleSave, editCategory,
  } = props;

  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState('');
  const [order, setOrder] = useState('');
  const [isZeroOrBelow, setIsZeroOrBelow] = useState(false);
  const { getLastOrderNumber } = useContext(CategoriesContext);
  const [lastOrder, setOrderNumber] = useState<number>(0);

  useEffect(() => {
    if (open) {
      // Se estivermos no modo de edição
      if (editCategory) {
        setName(editCategory.title);
        setDescription(editCategory.description);
        setOrder(editCategory.order);
      } else {
        // Se estivermos no modo de criação
        const fetchLastOrderNumber = async () => {
          try {
            const lastOrderNum = await getLastOrderNumber();
            setOrderNumber(lastOrderNum);
            setOrder(String(lastOrderNum + 1));
          } catch (error) {
            console.error('Error fetching last order number', error);
          }
        };

        fetchLastOrderNumber();
      }
    } else {
      setName('');
      setDescription('');
      setOrder(String(lastOrder + 1));
    }
  }, [open, getLastOrderNumber, lastOrder, editCategory]);

  useEffect(() => {
    if (order === '' || order == null) {
      return;
    }
    if (Number(order) <= 0 || String(order).startsWith('0')) {
      setIsZeroOrBelow(true);
    } else {
      setIsZeroOrBelow(false);
    }
  }, [order]);

  const handleOnSave = (title: string, description: string, order: string) => {
    // modalは閉じてある状態
    if (editCategory == null) {
      return;
    }

    // 新規作成
    if (editCategory === '') {
      handleSave({
        id: '',
        title,
        description,
        order,
      });
      return;
    }

    // 編集
    handleSave({
      ...editCategory, title, description, order,
    });
  };

  const title = useMemo(() => {
    if (editCategory === '') {
      return 'カテゴリ新規作成';
    }

    return 'カテゴリ編集';
  }, [editCategory]);

  const handleOnClose = () => {
    setName('');
    setDescription('');
    setOrder('');
    handleClose();
  };

  // デフォルト値を設定する
  useEffect(() => {
    // modalが閉じてある場合
    if (editCategory == null) {
      return;
    }

    // 新規作成の場合
    if (editCategory === '') {
      return;
    }

    setName(editCategory.title);
    setDescription(editCategory.description);
    setOrder(editCategory.order);
  }, [editCategory]);

  return (
    <>
      <ModalComponent
        title={title}
        confirmButtonText="保存する"
        onConfirm={() => handleOnSave(name, description, order)}
        show={open}
        closeModal={() => {
          handleOnClose();
        }}
        centered
        animation={false}
        isLackOfInfo={name.length === 0 || order.length === 0}
        scrollable
      >
        <form className="d-flex flex-column h-100">
          <div className="row">
            <div className="col-12">

              <div className="row g-3 mb-3">
                <label className="col-form-label col-sm-3 text-sm-start">
                  カテゴリ名
                  <span>*</span>
                </label>
                <div className="col-sm-9">
                  <div className="is-invalid" id="validatione1">
                    <input
                      type="text"
                      className="form-control"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="カテゴリ名を入力してください。"
                    />
                  </div>
                  <div className="invalid-feedback d-none" id="validationeFeedback1">
                    カテゴリ名を入力してください。
                  </div>
                </div>
              </div>
              <div className="row g-3 mb-3">
                <label className="col-form-label col-sm-3 text-sm-start">カテゴリ説明</label>
                <div className="col-sm-9">
                  <div className="is-invalid" id="validatione2">
                    <input
                      type="text"
                      className="form-control"
                      value={description || ''}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="カテゴリ説明を入力してください。"
                    />
                  </div>
                  <div className="invalid-feedback d-none" id="validationeFeedback2">
                    カテゴリ説明を入力してください。
                  </div>
                </div>
              </div>
              <div className="row g-3 mb-3">
                <label className="col-form-label col-sm-3 text-sm-start">
                  表示順
                  <span>*</span>
                </label>
                <div className="col-sm-9">
                  <div className="is-invalid" id="validatione2">
                    <input
                      type="number"
                      className="form-control"
                      min="1"
                      value={order}
                      onChange={(e) => setOrder(e.target.value)}
                      placeholder="表示順(1～)を入力してください。"
                    />
                    {
                      isZeroOrBelow && (
                        <div className="text-danger">0より大きい数値を入力してください</div>
                      )
                    }
                  </div>
                  <div className="invalid-feedback d-none" id="validationeFeedback2">表示順を入力してください。</div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </ModalComponent>
    </>
  );
};

export default NewCategoryModal;
